import React, { useState, useEffect } from 'react';

const CustomEventAdmin = () => {
    const [authenticated, setAuthenticated] = useState(false);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [config, setConfig] = useState({
        razorpayKeyId: '',
        razorpaySecret: '',
        emailTo: '',
        emailCc: '',
        isActive: true,
        formFields: [],
        ticketConfig: { categories: [], addons: [], dateRanges: [], pricingMatrix: {} }
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [adminRole, setAdminRole] = useState('');
    const [activeTab, setActiveTab] = useState('regConfig'); // 'regConfig', 'formFields', 'ticketConfig', 'amenities', 'workshopCapacities'

    // User management state
    const [users, setUsers] = useState([]);
    const [userForm, setUserForm] = useState({ newUsername: '', newPassword: '', newRole: 'check-in' });
    const [zipLoading, setZipLoading] = useState(false);

    // Amenity state
    const [amenities, setAmenities] = useState([]);
    const [amenityForm, setAmenityForm] = useState({ name: '', eventType: 'conference' });
    const [amenityLoading, setAmenityLoading] = useState(false);

    // Workshop capacities state
    const [workshopCapacities, setWorkshopCapacities] = useState({});
    const [capacitiesLoading, setCapacitiesLoading] = useState(false);
    const [capacitiesMessage, setCapacitiesMessage] = useState('');

    useEffect(() => {
        const storedAuth = localStorage.getItem('kapconAdminAuth');
        if (storedAuth) {
            try {
                const parsed = JSON.parse(storedAuth);
                setCredentials({ username: parsed.username, password: parsed.password });
                setAdminRole(parsed.role || '');
                setAuthenticated(true);
            } catch (e) {
                console.error(e);
            }
        }
    }, []);

    useEffect(() => {
        if (authenticated) {
            if (adminRole === 'full') {
                fetchConfig();
                fetchUsers();
                fetchAmenities();
                fetchWorkshopCapacities();
            }
        }
    }, [authenticated, adminRole]);

    const fetchAmenities = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/amenities`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) setAmenities(data.data || []);
        } catch (e) { console.error(e); }
    };

    const handleCreateAmenity = async (e) => {
        e.preventDefault();
        if (!amenityForm.name.trim()) return;
        setAmenityLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/amenities/create`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, ...amenityForm })
            });
            const data = await res.json();
            if (data.success) { setAmenityForm({ name: '', eventType: 'conference' }); fetchAmenities(); }
            else alert(data.message || 'Failed to create amenity');
        } catch { alert('Error connecting to server'); }
        finally { setAmenityLoading(false); }
    };

    const handleDeleteAmenity = async (id) => {
        if (!window.confirm('Remove this amenity from the system?')) return;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/amenities/${id}/delete`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) fetchAmenities();
            else alert(data.message || 'Failed to delete');
        } catch { alert('Error connecting to server'); }
    };

    const fetchWorkshopCapacities = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/workshop/capacities`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) setWorkshopCapacities(data.data || {});
        } catch (e) { console.error(e); }
    };

    const saveWorkshopCapacities = async () => {
        setCapacitiesLoading(true);
        setCapacitiesMessage('');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/workshop/capacities`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, capacities: workshopCapacities })
            });
            const data = await res.json();
            if (data.success) {
                setWorkshopCapacities(data.data);
                setCapacitiesMessage('Workshop capacities saved successfully');
            } else {
                setCapacitiesMessage(data.message || 'Error saving capacities');
            }
        } catch { setCapacitiesMessage('Error connecting to server'); }
        finally { setCapacitiesLoading(false); }
    };

    const handleLoginChange = (e) => {
        setCredentials(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleLogout = () => {
        localStorage.removeItem('kapconAdminAuth');
        setAuthenticated(false);
        setAdminRole('');
    };

    const fetchUsers = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/users/list`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                setUsers(data.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch users', error);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/users/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, ...userForm })
            });
            const data = await res.json();
            if (data.success) {
                setUserForm({ newUsername: '', newPassword: '', newRole: 'check-in' });
                fetchUsers();
            } else {
                alert(data.message || 'Failed to create user');
            }
        } catch (error) {
            alert('Error connecting to server');
        }
    };

    const handleDeleteUser = async (targetId) => {
        if (!window.confirm("Are you sure you want to delete this admin account?")) return;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/users/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, targetId })
            });
            const data = await res.json();
            if (data.success) {
                fetchUsers();
            } else {
                alert(data.message || 'Failed to delete user');
            }
        } catch (error) {
            alert('Error connecting to server');
        }
    };

    const downloadZip = async () => {
        setZipLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/download-tickets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Download failed');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = 'KAPCON_2026_MTickets.zip';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert(err.message || 'Failed to download zip');
        } finally {
            setZipLoading(false);
        }
    };

    const handleConfigChange = (e) => {
        const { name, value, type, checked } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/get-config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success && data.data) {
                setConfig({
                    razorpayKeyId: data.data.razorpayKeyId || '',
                    razorpaySecret: data.data.razorpaySecret || '',
                    emailTo: data.data.emailTo || '',
                    emailCc: data.data.emailCc && Array.isArray(data.data.emailCc) ? data.data.emailCc.join(', ') : '',
                    isActive: data.data.isActive ?? true,
                    formFields: data.data.formFields || [],
                    ticketConfig: data.data.ticketConfig || { categories: [], addons: [], dateRanges: [], pricingMatrix: {} }
                });
            }
        } catch (error) {
            console.error(error);
            // If fetch fails due to auth when using stored creds
            if (error.message?.includes('401')) {
                setAuthenticated(false);
                localStorage.removeItem('kapconAdminAuth');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                const authData = { username: credentials.username, password: credentials.password, role: data.role };
                localStorage.setItem('kapconAdminAuth', JSON.stringify(authData));
                setAdminRole(data.role);
                setAuthenticated(true);
            } else {
                setMessage(data.message || 'Invalid credentials');
                localStorage.removeItem('kapconAdminAuth');
            }
        } catch (error) {
            setMessage('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, ...config })
            });
            const data = await res.json();
            if (data.success) {
                setMessage('Configuration saved successfully');
            } else {
                setMessage(data.message || 'Error saving config');
            }
        } catch (error) {
            setMessage('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    if (!authenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Admin Login for KAPCON 2026
                    </h2>
                </div>
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        <form className="space-y-6" onSubmit={handleLogin}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Username</label>
                                <div className="mt-1">
                                    <input name="username" type="text" required value={credentials.username} onChange={handleLoginChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4013b5] focus:border-[#4013b5] sm:text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <div className="mt-1">
                                    <input name="password" type="password" required value={credentials.password} onChange={handleLoginChange} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4013b5] focus:border-[#4013b5] sm:text-sm" />
                                </div>
                            </div>
                            {message && <div className="text-red-600 text-sm">{message}</div>}
                            <div>
                                <button type="submit" disabled={loading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#4013b5] hover:bg-[#A02B00] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4013b5] disabled:bg-opacity-50">
                                    {loading ? 'Logging in...' : 'Log in'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">KAPCON 2026 Admin Config</h1>
                    <div className="flex gap-4">
                        <a href="/event/jss/admin/data" className="text-indigo-600 hover:text-indigo-900 text-sm font-medium self-center">View Participant Data &rarr;</a>
                        <button onClick={handleLogout} className="px-3 py-1.5 border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 rounded text-sm font-semibold">Logout</button>
                    </div>
                </div>

                {adminRole !== 'full' ? (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm text-red-700">Access Denied: You do not have full admin permissions to view or edit this configuration.</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Tabs Navigation */}
                        <div className="border-b border-gray-200 mb-6">
                            <nav className="-mb-px flex space-x-8">
                                <button
                                    onClick={() => setActiveTab('regConfig')}
                                    className={`${activeTab === 'regConfig' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                                >
                                    Req Config & Admins
                                </button>
                                <button
                                    onClick={() => setActiveTab('formFields')}
                                    className={`${activeTab === 'formFields' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                                >
                                    Form Fields
                                </button>
                                <button
                                    onClick={() => setActiveTab('ticketConfig')}
                                    className={`${activeTab === 'ticketConfig' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                                >
                                    Ticket Config
                                </button>
                                <button
                                    onClick={() => { setActiveTab('amenities'); fetchAmenities(); }}
                                    className={`${activeTab === 'amenities' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                                >
                                    Amenities
                                </button>
                                <button
                                    onClick={() => { setActiveTab('workshopCapacities'); fetchWorkshopCapacities(); }}
                                    className={`${activeTab === 'workshopCapacities' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                                >
                                    Workshop Slots
                                </button>
                            </nav>
                        </div>

                        {/* Tab Content: Reg Config */}
                        {activeTab === 'regConfig' && (
                            <>
                                <div className="bg-white shadow rounded-lg p-6 mb-8">
                                    <h2 className="text-xl font-semibold mb-6 pb-2 border-b">Registration Configuration</h2>

                                    <form onSubmit={handleSaveConfig} className="space-y-6">

                                        {/* Razorpay Section */}
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-medium text-gray-900 border-l-4 border-indigo-500 pl-3">Payment Gateway (Razorpay)</h3>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Key ID</label>
                                                    <input type="text" name="razorpayKeyId" value={config.razorpayKeyId} onChange={handleConfigChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="rzp_test_..." />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Key Secret</label>
                                                    <input type="password" name="razorpaySecret" value={config.razorpaySecret} onChange={handleConfigChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="••••••••••••" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t border-gray-200 mt-6 pt-6"></div>

                                        {/* Email Section */}
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-medium text-gray-900 border-l-4 border-indigo-500 pl-3">Notification Emails</h3>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Organizer Email (To)</label>
                                                    <input type="email" name="emailTo" value={config.emailTo} onChange={handleConfigChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="main.organizer@example.com" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">CC Emails (Comma separated)</label>
                                                    <input type="text" name="emailCc" value={config.emailCc} onChange={handleConfigChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="cc1@example.com, cc2@example.com" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t border-gray-200 mt-6 pt-6"></div>

                                        <div className="flex items-center">
                                            <input id="isActive" name="isActive" type="checkbox" checked={config.isActive} onChange={handleConfigChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
                                            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                                                Registration Form is Active (Publicly accessible)
                                            </label>
                                        </div>

                                        {message && (
                                            <div className={`p-4 rounded-md ${message.includes('success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                                {message}
                                            </div>
                                        )}

                                        <div className="flex justify-end">
                                            <button type="submit" disabled={loading} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                                                {loading ? 'Saving...' : 'Save Configuration'}
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <div className="bg-white shadow rounded-lg p-6 mb-8">
                                    <div className="flex justify-between items-center mb-6 pb-2 border-b">
                                        <h2 className="text-xl font-semibold">Admin Accounts Management</h2>
                                    </div>

                                    <div className="mb-6">
                                        <form onSubmit={handleCreateUser} className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 mb-1">Username</label>
                                                <input type="text" required placeholder="New username" value={userForm.newUsername} onChange={e => setUserForm({ ...userForm, newUsername: e.target.value })} className="block w-full border border-gray-300 rounded py-1.5 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 mb-1">Password</label>
                                                <input type="text" required placeholder="Password" value={userForm.newPassword} onChange={e => setUserForm({ ...userForm, newPassword: e.target.value })} className="block w-full border border-gray-300 rounded py-1.5 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 mb-1">Role</label>
                                                <select value={userForm.newRole} onChange={e => setUserForm({ ...userForm, newRole: e.target.value })} className="block w-full border border-gray-300 rounded py-1.5 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                                    <option value="check-in">Check-in Only</option>
                                                    <option value="full">Full Admin</option>
                                                </select>
                                            </div>
                                            <div>
                                                <button type="submit" className="w-full justify-center py-1.5 px-4 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700">Add User</button>
                                            </div>
                                        </form>
                                    </div>

                                    <div className="bg-gray-50 rounded border border-gray-200 overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {users.map((u, i) => (
                                                    <tr key={i}>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{u.username}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.role === 'full' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                                                                {u.role === 'full' ? 'Full Admin' : 'Check-in'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                            {u.username !== 'InsideOutAdmin' && (
                                                                <button onClick={() => handleDeleteUser(u._id)} className="text-red-600 hover:text-red-900">Delete</button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {users.length === 0 && <tr><td colSpan="3" className="px-4 py-3 text-sm text-gray-500 text-center">No additional admins found.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Tab Content: Form Fields */}
                        {activeTab === 'formFields' && (
                            <div className="bg-white shadow rounded-lg p-6 mb-8">
                                <FormFieldsEditor config={config} setConfig={setConfig} handleSaveConfig={handleSaveConfig} loading={loading} />
                            </div>
                        )}

                        {/* Tab Content: Ticket Config */}
                        {activeTab === 'ticketConfig' && (
                            <div className="bg-white shadow rounded-lg p-6 mb-8">
                                <TicketConfigEditor config={config} setConfig={setConfig} handleSaveConfig={handleSaveConfig} loading={loading} />
                            </div>
                        )}

                        {/* Tab Content: Workshop Slots */}
                        {activeTab === 'workshopCapacities' && (
                            <div className="bg-white shadow rounded-lg p-6 mb-8">
                                <WorkshopCapacitiesEditor
                                    capacities={workshopCapacities}
                                    setCapacities={setWorkshopCapacities}
                                    onSave={saveWorkshopCapacities}
                                    loading={capacitiesLoading}
                                    message={capacitiesMessage}
                                />
                            </div>
                        )}

                        {/* Tab Content: Amenities */}
                        {activeTab === 'amenities' && (
                            <div className="bg-white shadow rounded-lg p-6 mb-8">
                                <div className="flex justify-between items-center mb-6 pb-2 border-b">
                                    <div>
                                        <h2 className="text-xl font-semibold">Amenities Management</h2>
                                        <p className="text-sm text-gray-500 mt-1">Define items that can be marked as redeemed per participant during check-in scanning.</p>
                                    </div>
                                    <a href="/event/jss/admin/scan" target="_blank" rel="noreferrer"
                                        className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-sm font-medium hover:bg-indigo-100">
                                        Open Scan Page ↗
                                    </a>
                                </div>

                                {/* Add amenity form */}
                                <form onSubmit={handleCreateAmenity} className="flex flex-col sm:flex-row gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex-1">
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Amenity Name</label>
                                        <input
                                            type="text" required
                                            placeholder="e.g. Day 1 Breakfast, Delegate Kit, Day 2 Lunch..."
                                            value={amenityForm.name}
                                            onChange={e => setAmenityForm(prev => ({ ...prev, name: e.target.value }))}
                                            className="block w-full border border-gray-300 rounded py-1.5 px-3 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                    <div className="sm:w-40">
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Event Type</label>
                                        <select value={amenityForm.eventType} onChange={e => setAmenityForm(prev => ({ ...prev, eventType: e.target.value }))}
                                            className="block w-full border border-gray-300 rounded py-1.5 px-3 text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white">
                                            <option value="conference">Conference</option>
                                            <option value="workshop">Workshop</option>
                                            <option value="both">Both</option>
                                        </select>
                                    </div>
                                    <div className="sm:self-end">
                                        <button type="submit" disabled={amenityLoading}
                                            className="w-full sm:w-auto px-4 py-1.5 bg-indigo-600 text-white text-sm font-semibold rounded hover:bg-indigo-700 disabled:opacity-50">
                                            {amenityLoading ? 'Adding...' : '+ Add Amenity'}
                                        </button>
                                    </div>
                                </form>

                                {/* Amenities list */}
                                {amenities.length === 0 ? (
                                    <div className="text-center py-10 text-gray-400 text-sm">No amenities defined yet. Add one above.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {['conference', 'workshop', 'both'].map(type => {
                                            const group = amenities.filter(a => a.eventType === type);
                                            if (group.length === 0) return null;
                                            const labels = { conference: 'Conference', workshop: 'Workshop', both: 'Both (Conf & Workshop)' };
                                            const colors = { conference: 'bg-indigo-50 text-indigo-700 border-indigo-200', workshop: 'bg-amber-50 text-amber-700 border-amber-200', both: 'bg-green-50 text-green-700 border-green-200' };
                                            return (
                                                <div key={type} className="mb-4">
                                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{labels[type]}</h3>
                                                    <div className="space-y-1.5">
                                                        {group.map(a => (
                                                            <div key={a._id} className="flex items-center justify-between px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm">
                                                                <div className="flex items-center gap-3">
                                                                    <span className="text-lg">🎁</span>
                                                                    <span className="text-sm font-semibold text-gray-800">{a.name}</span>
                                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${colors[a.eventType]}`}>{labels[a.eventType]}</span>
                                                                </div>
                                                                <button onClick={() => handleDeleteAmenity(a._id)}
                                                                    className="text-xs text-red-500 hover:text-red-700 font-semibold px-2 py-1 hover:bg-red-50 rounded transition-colors">
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                                    <p className="text-xs text-blue-700 font-medium">💡 How it works:</p>
                                    <ul className="text-xs text-blue-600 mt-1 space-y-0.5 list-disc list-inside">
                                        <li>Amenities appear on the scan page when a participant's QR code is scanned</li>
                                        <li>Any admin (check-in or full) can mark amenities as redeemed — once checked, it's locked</li>
                                        <li>Only full admins can undo a redemption</li>
                                        <li>First scan auto-checks-in the participant</li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// --- Helper Components for Tabs --- //

const FormFieldsEditor = ({ config, setConfig, handleSaveConfig, loading }) => {
    const fields = config.formFields || [];

    const addField = () => {
        const newField = {
            id: Date.now().toString(),
            name: `field_${Date.now()}`,
            label: 'New Field',
            type: 'text', // text, tel, email, select, radio, checkbox
            required: false,
            halfWidth: false,
            options: '', // comma separated for select/radio
            isGovId: false,
            forgotIdUrl: ''
        };
        setConfig(prev => ({ ...prev, formFields: [...prev.formFields, newField] }));
    };

    const removeField = (id) => {
        setConfig(prev => ({ ...prev, formFields: prev.formFields.filter(f => f.id !== id) }));
    };

    const updateField = (id, key, value) => {
        setConfig(prev => {
            const updated = prev.formFields.map(f => {
                if (f.id === id) {
                    return { ...f, [key]: value };
                }
                return f;
            });
            return { ...prev, formFields: updated };
        });
    };

    const moveField = (index, dir) => {
        if (index + dir < 0 || index + dir >= fields.length) return;
        const updated = [...fields];
        const temp = updated[index];
        updated[index] = updated[index + dir];
        updated[index + dir] = temp;
        setConfig(prev => ({ ...prev, formFields: updated }));
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6 pb-2 border-b">
                <div>
                    <h2 className="text-xl font-semibold">Form Fields Builder</h2>
                    <p className="text-sm text-gray-500">Add custom fields for your registration form.</p>
                </div>
                <button type="button" onClick={addField} className="px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-100 text-sm font-medium">
                    + Add Field
                </button>
            </div>

            <div className="space-y-4 mb-6">
                {fields.length === 0 && <p className="text-gray-500 text-sm italic">No custom fields defined. Click "Add Field" to start.</p>}

                {fields.map((field, index) => (
                    <div key={field.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm flex flex-col gap-4">
                        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-gray-400">#{index + 1}</span>
                                <input
                                    type="text"
                                    value={field.name}
                                    onChange={(e) => updateField(field.id, 'name', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                                    className="text-xs font-mono text-gray-700 bg-white border border-gray-300 rounded px-2 py-1 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                    title="Internal Field Name (Database Key)"
                                    placeholder="e.g. designation"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => moveField(index, -1)} disabled={index === 0} className="text-gray-400 hover:text-indigo-600 disabled:opacity-30">↑</button>
                                <button type="button" onClick={() => moveField(index, 1)} disabled={index === fields.length - 1} className="text-gray-400 hover:text-indigo-600 disabled:opacity-30">↓</button>
                                <button type="button" onClick={() => removeField(field.id)} className="text-red-400 hover:text-red-600 ml-4">✕</button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="lg:col-span-2">
                                <label className="block text-xs font-medium text-gray-700">Field Label</label>
                                <input type="text" value={field.label} onChange={(e) => updateField(field.id, 'label', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md py-1.5 px-3 text-sm" placeholder="Ex: Designation" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700">Type</label>
                                <select value={field.type} onChange={(e) => updateField(field.id, 'type', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md py-1.5 px-3 text-sm">
                                    <option value="text">Short Text</option>
                                    <option value="textarea">Long Text</option>
                                    <option value="email">Email</option>
                                    <option value="tel">Phone</option>
                                    <option value="select">Dropdown (Select)</option>
                                    <option value="radio">Radio Buttons</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-2 pt-6">
                                <label className="flex items-center">
                                    <input type="checkbox" checked={field.required} onChange={(e) => updateField(field.id, 'required', e.target.checked)} className="h-4 w-4 text-indigo-600" />
                                    <span className="ml-2 text-sm text-gray-700">Required Field</span>
                                </label>
                                <label className="flex items-center">
                                    <input type="checkbox" checked={field.halfWidth} onChange={(e) => updateField(field.id, 'halfWidth', e.target.checked)} className="h-4 w-4 text-indigo-600" />
                                    <span className="ml-2 text-sm text-gray-700">Half Width (50%)</span>
                                </label>
                            </div>
                        </div>

                        {(field.type === 'select' || field.type === 'radio') && (
                            <div>
                                <label className="block text-xs font-medium text-gray-700">Options (comma separated)</label>
                                <input type="text" value={field.options} onChange={(e) => updateField(field.id, 'options', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md py-1.5 px-3 text-sm" placeholder="Option 1, Option 2, Option 3" />
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-purple-50 p-3 rounded border border-purple-100">
                            <label className="flex items-center lg:col-span-2">
                                <input type="checkbox" checked={field.isGovId} onChange={(e) => updateField(field.id, 'isGovId', e.target.checked)} className="h-4 w-4 text-purple-600 rounded border-purple-300" />
                                <span className="ml-2 text-sm text-gray-800 font-medium">Is Gov ID/Registration Number Field?</span>
                            </label>
                            {field.isGovId && (
                                <div className="lg:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700">"Forgot ID" Redirect Link (Optional)</label>
                                    <input type="url" value={field.forgotIdUrl} onChange={(e) => updateField(field.id, 'forgotIdUrl', e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md py-1.5 px-3 text-sm" placeholder="https://example.com/forgot" />
                                </div>
                            )}
                        </div>

                        {config.ticketConfig?.categories?.length > 0 && (
                            <div className="mt-2 pt-3 border-t border-gray-200">
                                <label className="block text-xs font-medium text-gray-700 mb-2 border-b pb-1">Category-Specific Rule Overrides</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {config.ticketConfig.categories.map(cat => {
                                        const rule = field.categoryRules?.[cat.id] || 'default';
                                        return (
                                            <div key={cat.id} className="flex items-center justify-between bg-white px-2 py-1.5 border border-gray-200 shadow-sm rounded-md text-sm">
                                                <span className="text-gray-600 truncate mr-2 font-medium" title={cat.name}>{cat.name}</span>
                                                <select
                                                    value={rule}
                                                    onChange={(e) => {
                                                        const newRules = { ...(field.categoryRules || {}) };
                                                        if (e.target.value === 'default') delete newRules[cat.id];
                                                        else newRules[cat.id] = e.target.value;
                                                        updateField(field.id, 'categoryRules', newRules);
                                                    }}
                                                    className="border-gray-300 bg-gray-50 focus:bg-white rounded text-xs py-1 px-2 min-w-[90px]"
                                                >
                                                    <option value="default">No Override</option>
                                                    <option value="mandatory">Make Required</option>
                                                    <option value="optional">Make Optional</option>
                                                    <option value="hidden">Hidden completely</option>
                                                </select>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                    </div>
                ))}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200">
                <button onClick={handleSaveConfig} disabled={loading} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                    {loading ? 'Saving...' : 'Save Fields Configuration'}
                </button>
            </div>
        </div>
    );
};

// --- Ticket Config Editor --- //
const TicketConfigEditor = ({ config, setConfig, handleSaveConfig, loading }) => {
    const tConf = {
        categories: config.ticketConfig?.categories || [],
        addons: config.ticketConfig?.addons || [],
        dateRanges: config.ticketConfig?.dateRanges || [],
        pricingMatrix: config.ticketConfig?.pricingMatrix || {}
    };

    const updateListOption = (key, index, field, value) => {
        setConfig(prev => {
            const prevTConf = prev.ticketConfig || {};
            const list = [...(prevTConf[key] || [])];
            if (list[index]) {
                list[index] = { ...list[index], [field]: value };
            }
            return { ...prev, ticketConfig: { ...prevTConf, [key]: list } };
        });
    };

    const addListOption = (key, template) => {
        setConfig(prev => {
            const prevTConf = prev.ticketConfig || {};
            const list = prevTConf[key] || [];
            return { ...prev, ticketConfig: { ...prevTConf, [key]: [...list, template] } };
        });
    };

    const removeListOption = (key, index) => {
        setConfig(prev => {
            const prevTConf = prev.ticketConfig || {};
            const list = prevTConf[key] || [];
            return { ...prev, ticketConfig: { ...prevTConf, [key]: list.filter((_, i) => i !== index) } };
        });
    };

    const updateMatrix = (catId, dateId, price) => {
        setConfig(prev => {
            const prevTConf = prev.ticketConfig || {};
            const matrix = { ...(prevTConf.pricingMatrix || {}) };
            if (!matrix[catId]) matrix[catId] = {};
            matrix[catId] = { ...matrix[catId], [dateId]: price };
            return { ...prev, ticketConfig: { ...prevTConf, pricingMatrix: matrix } };
        });
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6 pb-2 border-b">
                <div>
                    <h2 className="text-xl font-semibold">Ticket & Pricing Config</h2>
                    <p className="text-sm text-gray-500">Manage categories, date ranges, base prices, and conditional add-ons.</p>
                </div>
            </div>

            {/* Date Ranges */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-800">1. Date Ranges (Pricing Tiers)</h3>
                    <button type="button" onClick={() => addListOption('dateRanges', { id: Date.now().toString(), label: '', deadline: '', isSpot: false })} className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded text-xs font-semibold">+ Add Date Range</button>
                </div>
                <div className="space-y-3 pl-4 border-l-2 border-gray-200">
                    {tConf.dateRanges.length === 0 && <p className="text-sm text-gray-500 italic">No date ranges defined.</p>}
                    {tConf.dateRanges.map((dr, idx) => (
                        <div key={dr.id} className="flex flex-col sm:flex-row gap-3 items-end p-3 bg-gray-50 rounded border border-gray-100">
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500 mb-1">Date Range Label</label>
                                <input type="text" value={dr.label} onChange={(e) => updateListOption('dateRanges', idx, 'label', e.target.value)} placeholder="e.g. 30th April 2026" className="w-full text-sm py-1.5 px-3 border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />
                            </div>
                            <div className="w-full sm:w-auto">
                                <label className="block text-xs text-gray-500 mb-1">Deadline Date</label>
                                <input type="date" value={dr.deadline || ''} onChange={(e) => updateListOption('dateRanges', idx, 'deadline', e.target.value)} className="w-full text-sm py-1.5 px-3 border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />
                            </div>
                            <label className="flex items-center gap-2 pb-2">
                                <input type="checkbox" checked={dr.isSpot} onChange={(e) => updateListOption('dateRanges', idx, 'isSpot', e.target.checked)} className="text-indigo-600 rounded" />
                                <span className="text-sm text-gray-700">Is Spot Registration?</span>
                            </label>
                            <button type="button" onClick={() => removeListOption('dateRanges', idx)} className="text-red-500 hover:text-red-700 p-2 text-sm font-bold">✕</button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Categories */}
            <div className="mb-8 border-t pt-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-800">2. Ticket Categories</h3>
                    <button type="button" onClick={() => addListOption('categories', { id: Date.now().toString(), name: '', isForeign: false, requiresHodCert: false, requiresIdNumber: false, idNumberLabel: '' })} className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded text-xs font-semibold">+ Add Category</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tConf.categories.length === 0 && <p className="text-sm text-gray-500 italic">No categories defined.</p>}
                    {tConf.categories.map((cat, idx) => (
                        <div key={cat.id} className="flex flex-col bg-white border border-gray-300 rounded-lg p-4 shadow-sm relative">
                            <button type="button" onClick={() => removeListOption('categories', idx)} className="absolute top-2 right-2 text-red-400 hover:text-red-600 font-bold">✕</button>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Category Name</label>
                            <input type="text" value={cat.name} onChange={(e) => updateListOption('categories', idx, 'name', e.target.value)} placeholder="e.g. Member Consultants" className="w-full text-sm py-1.5 px-3 border border-gray-300 rounded mb-3" />

                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={cat.isForeign} onChange={(e) => updateListOption('categories', idx, 'isForeign', e.target.checked)} className="text-indigo-600 rounded" />
                                <span className="text-sm text-gray-700">Foreign Dollar Pricing?</span>
                            </label>
                            <label className="flex items-center gap-2 mt-2">
                                <input type="checkbox" checked={cat.requiresHodCert} onChange={(e) => updateListOption('categories', idx, 'requiresHodCert', e.target.checked)} className="text-indigo-600 rounded" />
                                <span className="text-sm text-gray-700">Requires HOD Certificate?</span>
                            </label>
                            <label className="flex items-center gap-2 mt-2">
                                <input type="checkbox" checked={cat.requiresIdNumber} onChange={(e) => updateListOption('categories', idx, 'requiresIdNumber', e.target.checked)} className="text-indigo-600 rounded" />
                                <span className="text-sm text-gray-700">Requires ID Number?</span>
                            </label>
                            {cat.requiresIdNumber && (
                                <input type="text" value={cat.idNumberLabel || ''} onChange={(e) => updateListOption('categories', idx, 'idNumberLabel', e.target.value)} placeholder="ID Label (e.g., KMC No.)" className="w-full text-sm py-1.5 px-3 border border-gray-300 rounded mt-2" />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Pricing Matrix */}
            {tConf.categories.length > 0 && tConf.dateRanges.length > 0 && (
                <div className="mb-8 border-t pt-8">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">3. Base Pricing Matrix</h3>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 text-sm border-collapse">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold text-gray-600 bg-gray-100 border-r">Category \\ Date</th>
                                    {tConf.dateRanges.map(dr => (
                                        <th key={dr.id} className="px-4 py-3 text-center font-semibold text-gray-600 border-r">{dr.label || 'Unnamed Date'} {dr.isSpot && '(Spot)'}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {tConf.categories.map(cat => (
                                    <tr key={cat.id}>
                                        <td className="px-4 py-3 font-medium text-gray-900 bg-gray-50 border-r">{cat.name || 'Unnamed'} {cat.isForeign && '(USD)'}</td>
                                        {tConf.dateRanges.map(dr => (
                                            <td key={dr.id} className="px-4 py-2 border-r text-center">
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    value={tConf.pricingMatrix[cat.id]?.[dr.id] || ''}
                                                    onChange={e => updateMatrix(cat.id, dr.id, e.target.value)}
                                                    className="w-24 text-center py-1 border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Add-ons */}
            <div className="mb-8 border-t pt-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-800">4. Conditional Add-ons</h3>
                    <button type="button" onClick={() => addListOption('addons', { id: Date.now().toString(), name: '', label: '', price: 0, excludedCategories: [], isForeign: false })} className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded text-xs font-semibold">+ Add Add-on</button>
                </div>
                <div className="space-y-4">
                    {tConf.addons.length === 0 && <p className="text-sm text-gray-500 italic">No add-ons defined.</p>}
                    {tConf.addons.map((addon, aIdx) => (
                        <div key={addon.id} className="border border-indigo-100 rounded-xl p-5 bg-[#fcfcff] relative">
                            <button type="button" onClick={() => removeListOption('addons', aIdx)} className="absolute top-4 right-4 text-red-400 hover:text-red-600 font-bold">✕</button>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Add-on Name (Internal)</label>
                                    <input type="text" value={addon.name} onChange={(e) => updateListOption('addons', aIdx, 'name', e.target.value)} className="w-full text-sm py-1.5 px-3 border border-gray-300 rounded" placeholder="e.g. accompanyingSpouse" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Form Label (Shown to user)</label>
                                    <input type="text" value={addon.label} onChange={(e) => updateListOption('addons', aIdx, 'label', e.target.value)} className="w-full text-sm py-1.5 px-3 border border-gray-300 rounded" placeholder="e.g. Accompanying Spouse / Child" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Flat Price</label>
                                    <input type="number" value={addon.price} onChange={(e) => updateListOption('addons', aIdx, 'price', e.target.value)} className="w-full text-sm py-1.5 px-3 border border-gray-300 rounded" placeholder="0" />
                                </div>
                                <div>
                                    <label className="block text-xs text-transparent mb-1">.</label>
                                    <label className="flex items-center gap-2 mt-2">
                                        <input type="checkbox" checked={addon.isForeign} onChange={(e) => updateListOption('addons', aIdx, 'isForeign', e.target.checked)} className="text-indigo-600 rounded" />
                                        <span className="text-sm text-gray-700">Is this price in USD?</span>
                                    </label>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <label className="block text-xs font-semibold text-gray-700 mb-2">Exclude this add-on for the following Categories:</label>
                                <p className="text-xs text-gray-500 mb-3">If checked, users choosing this category won't see this add-on.</p>
                                <div className="flex flex-wrap gap-4">
                                    {tConf.categories.length === 0 && <span className="text-xs text-gray-400">No categories to exclude.</span>}
                                    {tConf.categories.map(cat => {
                                        const isExcluded = addon.excludedCategories?.includes(cat.id);
                                        return (
                                            <label key={cat.id} className={`flex items-center gap-2 border px-3 py-1.5 rounded cursor-pointer text-sm ${isExcluded ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <input
                                                    type="checkbox"
                                                    className={`rounded ${isExcluded ? 'text-red-500 h-4 w-4' : 'text-gray-400'}`}
                                                    checked={!!isExcluded}
                                                    onChange={(e) => {
                                                        const currentExc = addon.excludedCategories || [];
                                                        const newExc = e.target.checked
                                                            ? [...currentExc, cat.id]
                                                            : currentExc.filter(id => id !== cat.id);
                                                        updateListOption('addons', aIdx, 'excludedCategories', newExc);
                                                    }}
                                                />
                                                {cat.name || 'Unnamed'}
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200">
                <button onClick={handleSaveConfig} disabled={loading} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                    {loading ? 'Saving...' : 'Save Ticket Configuration'}
                </button>
            </div>
        </div>
    );
};

const WORKSHOP_LABELS = {
    '1': 'Coagulation Insights',
    '2': '"Peak-a-boo" – Detecting hemoglobinopathy by HPLC',
    '3': 'Hands on Flow Cytometry in Leukemia diagnosis',
    '4': 'Immunohematology & Apheresis: From Technique to therapy',
    '5': 'Procedural Skills – Bone marrow biopsy, Core Biopsy and FNAC',
    '6': 'A journey through the gut – Interpretation of GI biopsies',
    '7': 'Understanding Immunohistochemistry (IHC)',
    '8': 'Utility of Immunofluorescence in Nephropathology & Dermatopathology',
    '9': 'Digital pathology in practice – From Glass slides to AI-Ready Workflows',
    '10': 'Mastering ANA-IF on HEp2 & Primate Liver (ICAP 2024-25)'
};

const WorkshopCapacitiesEditor = ({ capacities, setCapacities, onSave, loading, message }) => {
    const handleChange = (id, value) => {
        const num = parseInt(value, 10);
        setCapacities(prev => ({ ...prev, [id]: isNaN(num) ? 0 : Math.max(0, num) }));
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6 pb-2 border-b">
                <div>
                    <h2 className="text-xl font-semibold">Workshop Slot Capacities</h2>
                    <p className="text-sm text-gray-500 mt-1">Set the maximum number of registrations allowed per workshop.</p>
                </div>
            </div>

            <div className="space-y-3 mb-6">
                {Object.keys(WORKSHOP_LABELS).map(id => (
                    <div key={id} className="flex items-center justify-between gap-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">{id}</span>
                            <span className="text-sm font-medium text-gray-700 truncate" title={WORKSHOP_LABELS[id]}>{WORKSHOP_LABELS[id]}</span>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                            <label className="text-xs text-gray-500 font-semibold">Total Slots</label>
                            <input
                                type="number"
                                min="0"
                                value={capacities[id] ?? ''}
                                onChange={e => handleChange(id, e.target.value)}
                                className="w-20 text-center py-1.5 border border-gray-300 rounded-md text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                    </div>
                ))}
            </div>

            {message && (
                <div className={`mb-4 p-3 rounded-md text-sm ${message.includes('success') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {message}
                </div>
            )}

            <div className="flex justify-end pt-4 border-t border-gray-200">
                <button onClick={onSave} disabled={loading} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                    {loading ? 'Saving...' : 'Save Capacities'}
                </button>
            </div>
        </div>
    );
};

export default CustomEventAdmin;
